import { useEffect, useState } from "react"
import { AdminEntryClient } from "@/domain/admin/client/AdminEntryClient"
import type { WeightClassOption } from "@/domain/admin/model/AdminEntry"
import type { EntryDetail } from "@/domain/entry/model/Entry"

interface WeightClassChoice {
  playerId: string
  candidates: WeightClassOption[]
  allOfSport: WeightClassOption[]
}

/**
 * 관리자 신청 상세 훅.
 *
 * 체급 확정 · 선수 승인 · 신청 상태 전이를 한 화면에서 처리하고, 변경 후에는 상세를 다시 읽는다
 * (화면이 서버 상태를 추측해 그리지 않는다).
 */
export function useAdminEntryDetail(entryId: string) {
  const [detail, setDetail] = useState<EntryDetail | null>(null)
  const [weightClassChoice, setWeightClassChoice] =
    useState<WeightClassChoice | null>(null)
  const [isLoaded, setIsLoaded] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [errorMessage, setErrorMessage] = useState("")

  async function fetchDetail() {
    setIsLoaded(false)

    try {
      const result = await AdminEntryClient.getById(entryId)
      setDetail(result.entry)
      setErrorMessage("")
    } catch (error) {
      setDetail(null)
      setErrorMessage(
        error instanceof Error
          ? error.message
          : "신청 내역을 불러오지 못했습니다.",
      )
    } finally {
      setIsLoaded(true)
    }
  }

  /** 체급 후보를 열어 둔다 (선수마다 한 번만 조회한다) */
  async function openWeightClassChoice(playerId: string) {
    setIsSaving(true)

    try {
      const result = await AdminEntryClient.getWeightClassCandidate(playerId)
      setWeightClassChoice({
        playerId,
        candidates: result.candidates,
        allOfSport: result.allOfSport,
      })
      setErrorMessage("")
    } catch (error) {
      setErrorMessage(
        error instanceof Error
          ? error.message
          : "체급 후보를 불러오지 못했습니다.",
      )
    } finally {
      setIsSaving(false)
    }
  }

  function closeWeightClassChoice() {
    setWeightClassChoice(null)
  }

  async function classifyWeightClass(option: WeightClassOption) {
    if (!weightClassChoice) return

    setIsSaving(true)

    try {
      await AdminEntryClient.classifyWeightClass(
        weightClassChoice.playerId,
        option.payload,
      )
      setWeightClassChoice(null)
      await fetchDetail()
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : "체급 확정에 실패했습니다.",
      )
    } finally {
      setIsSaving(false)
    }
  }

  async function updatePlayerStatus(
    playerId: string,
    status: string,
    rejectedReason = "",
  ) {
    setIsSaving(true)

    try {
      await AdminEntryClient.updatePlayerStatus(
        playerId,
        status,
        rejectedReason,
      )
      await fetchDetail()
    } catch (error) {
      setErrorMessage(
        error instanceof Error
          ? error.message
          : "선수 상태 변경에 실패했습니다.",
      )
    } finally {
      setIsSaving(false)
    }
  }


  useEffect(() => {
    void fetchDetail()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entryId])

  return {
    detail,
    weightClassChoice,
    isLoaded,
    isSaving,
    errorMessage,
    openWeightClassChoice,
    closeWeightClassChoice,
    classifyWeightClass,
    updatePlayerStatus,
  }
}
